import { ClassNamesContent, Theme, css } from '@emotion/react';
import { NumberOfMonthsResponsive } from 'omakase/DatePicker/DatePicker';

import { ClassNames } from 'react-day-picker';

const rangeEndpointRadius = '8px';
const cellSize = '48px';
const CAPTION_MIN_HEIGHT = 44;

export const caption = ({ spacing }: Theme) => css`
  align-items: center;
  display: flex;
  flex-direction: row;
  margin-bottom: ${spacing(1.5)}px;
  user-select: none;
  min-height: ${CAPTION_MIN_HEIGHT}px;
`;

export const laterMonthButton = ({ spacing }: Theme) => css`
  margin-left: ${spacing(1.5)}px;
`;

// Really this style is  applied to <th> elements within a <tr>, but the relevant
// styles are all inherited from parent element to child.
export const headCellRowCss = ({ colors: { fgSubtle }, typography: { variant } }: Theme) => css`
  color: ${fgSubtle};
  ${variant('body02')};
  // Override variant's line-height to match the cell height,
  // this will center it vertically.
  line-height: ${cellSize};
  height: ${cellSize};
  user-select: none;
`;

const rangeSelectionContinues = (rightOrLeft: 'right' | 'left', color: string) =>
  /*
   * The gradient to "transparent" here needs to use rg ba(255, 255, 255, 0) instead,
   * otherwise this will look like a bug in Safari. See
   * https://stackoverflow.com/questions/38391457/linear-gradient-to-transparent-bug-in-latest-safari
   * for more discussion. */
  css`
    background: center/ ${cellSize} ${cellSize}
      linear-gradient(to ${rightOrLeft}, ${color}, rgba(255, 255, 255, 0));
  `;

const continuedRangeBeyond = (rightOrLeft: 'right' | 'left', color: string) => css`
  position: relative;
  overflow: visible;

  &::after {
    content: '';
    height: ${cellSize};
    width: ${cellSize};
    position: absolute;
    ${rightOrLeft}: -${cellSize};
    top: 0;
    ${rangeSelectionContinues(rightOrLeft, color)}
  }
`;

const multiMonthLayout = (
  { breakpoints, spacing }: Theme,
  { xs, sm = 1 }: NumberOfMonthsResponsive
) => {
  const isMultiMonthMobile = xs > 1;
  const isMultiMonthDesktop = sm > 1;

  return [
    isMultiMonthMobile &&
      css`
        ${breakpoints.down('xs')} {
          display: flex;
          flex-direction: column;
        }
      `,
    !isMultiMonthDesktop &&
      css`
        ${breakpoints.up('xs')} {
          & > * {
            margin: 0;
          }
        }
      `,
  ];
};

const multiMonthLimits = (
  { breakpoints, spacing }: Theme,
  { xs, sm = 1 }: NumberOfMonthsResponsive
) => {
  const isMultiMonthMobile = xs > 1;
  const isMultiMonthDesktop = sm > 1;

  const lastSmMonthInViewIndexFromEnd = sm >= xs ? 1 : xs - sm + 1;

  return [
    isMultiMonthMobile &&
      css`
        ${breakpoints.down('xs')} {
          // Hide prev / next buttons in favor of vertical scrolling
          & > div > button {
            display: none;
          }
        }
      `,
    isMultiMonthDesktop &&
      css`
        ${breakpoints.up('sm')} {
          // Hide prev / next buttons for all but last month in view
          &:not(:nth-last-of-type(${lastSmMonthInViewIndexFromEnd})) > div > button:last-of-type {
            display: none;
          }
          &:not(:first-of-type) > div > button:first-of-type {
            display: none;
          }
        }
      `,
    (isMultiMonthMobile || isMultiMonthDesktop) &&
      css`
        ${breakpoints.down('xs')} {
          margin: 0 0 ${spacing(5)}px;

          // If mobile should display less months than the total number rendered
          // (which is the greater of the xs # and the sm #), hide months as appropriate
          &:nth-of-type(n + ${xs + 1}) {
            display: none;
          }
        }

        ${breakpoints.up('sm')} {
          // If desktop should display less months than the total number rendered
          // (which is the greater of the xs # and the sm #), hide months as appropriate
          &:nth-of-type(n + ${sm + 1}) {
            display: none;
          }

          // Remove any right margin on the last visible month
          &:nth-last-of-type(${lastSmMonthInViewIndexFromEnd}) {
            margin-right: 0;
          }
        }
      `,
  ];
};

/**
 * Gets assigned to the classNames= prop of a <DayPicker> component.
 * cssFunc should come from the {css} property of an emotion <ClassNames> render props child.
 */
export function computeClassNames(
  cssFunc: ClassNamesContent['css'],
  theme: Theme,
  numberOfMonths: NumberOfMonthsResponsive
): ClassNames {
  const {
    colors,
    interactiveStates: { active, disabled, focus, hover },
    spacing,
    typography: { variant },
  } = theme;

  const midRangeColor = colors.bgPrimaryLight;

  return {
    caption_label: cssFunc`
      color: ${colors.fgDefault};
      display: flex;
      align-items: center;
      ${variant('heading04')};
      margin-right: auto;
    `,
    cell: cssFunc`
      & {
        --rdp-cell-size: ${cellSize};
      }
    `,
    root: cssFunc`
      & .rdp-table {
        width: 100%;
        max-width: 100%;
        table-layout: fixed;
      }

      & .rdp-months {
        width: 100%;

        ${multiMonthLayout(theme, numberOfMonths)}
      }

      & .rdp-month {
        width: 100%;

        ${multiMonthLimits(theme, numberOfMonths)}
      }

      & .rdp-caption {
        margin: 0 auto ${spacing(1.5)}px 0;
      }

      & .rdp-dropdown_year {
        margin-left: ${spacing(1)}px;
      }

      & .rdp-dropdown:focus + * {
        box-shadow: 0 0 0 2px ${colors.borderPrimaryBase};
      }

      /* including .rdp-button here too is to increase specificity */
      & .rdp-day.rdp-button {

        --rdp-background-color: initial;
        --rdp-outline: initial;
        --rdp-accent-color: initial;


        ${variant('body02Strong')};
        // However, we need to override the line height we get from the above,
        // otherwise centering of the day numbers gets thrown off:
        line-height: normal;
        border-radius: ${rangeEndpointRadius};
        border: none;
        width: 100%;
        max-width: 100%;


        ${hover(css`
          background: ${colors.bgLighter};
        `)};

        ${active(css`
          background: ${colors.bgLight};
        `)};

        ${focus(css`
          box-shadow: inset 0 0 0 2px ${colors.borderPrimaryBase};
        `)};

        &.rdp-day_selected {
          cursor: auto;
          &:not(.rdp-day_range_middle) {
            background: ${colors.bgPrimaryBase};

            &.rdp-day_range_start:not(.rdp-day_range_end) {
              border-top-right-radius: 0;
              border-bottom-right-radius: 0;
            }
            &.rdp-day_range_end:not(.rdp-day_range_start) {
              border-top-left-radius: 0;
              border-bottom-left-radius: 0;
            }


            ${hover(css`
              background: ${colors.bgPrimaryDark};
            `)};

            ${active(css`
              background: ${colors.bgPrimaryDark};
            `)};
          }

          /* When a range spans a month boundary, we add some pseudocontent
           * via css to show the range extending earlier or later in time. This is true
           * for middle days, for start-but-not-end days, and for end-but-not-start days.
           * If day is at the very beginning or end of the row, though, there is no space to do
           * that. */
          &.rdp-day_range_middle, &.rdp-day_range_end:not(.rdp-day_range_start) {
            &.rdp-day_first_in_month:not(.rdp-day_first_in_month_and_fully_left) {
              ${continuedRangeBeyond('left', midRangeColor)};
            }
          }

          &.rdp-day_range_middle, &.rdp-day_range_start:not(.rdp-day_range_end) {
            &.rdp-day_last_in_month:not(.rdp-day_last_in_month_and_fully_right) {
              ${continuedRangeBeyond('right', midRangeColor)};
            }
          }

          &.rdp-day_range_middle {
            background: ${midRangeColor};
            border-radius: 0;
            color: ${colors.fgDefault};

            /* If a middle day is in the "corner" of a month, however, we apply a gradient
             * to the background of the day itself. We don't do this for range_start
             * or for range_end, just for middle days specifically. */
            &.rdp-day_first_in_month_and_fully_left {
              ${rangeSelectionContinues('left', midRangeColor)};
            }

            &.rdp-day_last_in_month_and_fully_right {
              ${rangeSelectionContinues('right', midRangeColor)};
            }
          }
        }

        /* Add a dot for today */
        &.rdp-day_today {
          --today-dot-color: ${colors.fgPrimaryBase};
          position: relative;

          /* We use ::before content here 'cause there may be ::after content on the same
           * node related to the continued-selection hint. */
          &::before {
            content: '';
            position: absolute;
            background-color: var(--today-dot-color);
            width: 5px;
            height: 5px;
            border-radius: 2.5px;
            left: calc(50% - 2.5px);
            // Mocks specify the dot should be 35px from the top
            // of the calendar box, but we subtract off 2px to account
            // for the border thickness.
            top: 33px;
          }
        }

        /* Override today-dot-color in the case of selection. */
        &.rdp-day_today.rdp-day_selected:not(.rdp-day_range_middle) {
          --today-dot-color: ${colors.fgContrast};
        }

        /* Upstream styling does this with opacity; we use another approach,
         * and add a strikethrough.
         * IMPORTANT NOTE: use "css" for the Interpolation within the
         * interactiveStates helpers here, not "cssFunc" */
        ${disabled(css`
          opacity: 1;
          color: ${colors.fgDisabled};
          text-decoration: line-through;
          ${variant('body02Strong')};
          // However, we need to override the line height we get from the above,
          // otherwise centering of the day numbers gets thrown off:
          line-height: normal;
        `)};

      }
    `,
  };
}
